import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';

const Tooltip = ({
    children,
    tooltipText,
    width,
    variant = 'dark',
}: {
    children?: ReactNode;
    tooltipText: React.ReactNode;
    width?: string;
    variant?: 'dark' | 'light';
}) => {
    const [pos, setPos] = React.useState<{ top: number; left: number; flip: boolean } | null>(null);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const tooltipId = React.useRef(`tooltip-${Math.random().toString(36).slice(2)}`);

    function handleMouseEnter() {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPos({ top: rect.top + rect.height / 2, left: rect.right + 8, flip: false });
        }
    }

    function handleMouseLeave() {
        setPos(null);
    }

    React.useLayoutEffect(() => {
        if (pos && tooltipRef.current && triggerRef.current) {
            const tooltipWidth = tooltipRef.current.offsetWidth;
            const rect = triggerRef.current.getBoundingClientRect();
            const wouldOverflow = rect.right + tooltipWidth + 8 > window.innerWidth - 16;
            if (wouldOverflow && !pos.flip) {
                setPos({ top: pos.top, left: rect.left - 8, flip: true });
            }
        }
    }, [pos]);

    if (!children) return null;

    const isVisible = pos !== null;

    const tooltip = ReactDOM.createPortal(
        <div
            id={tooltipId.current}
            role="tooltip"
            ref={tooltipRef}
            className={classNames(
                'fixed z-50 text-xs whitespace-normal px-4 py-2 rounded flex items-start transition-opacity duration-150',
                variant === 'light'
                    ? 'bg-white text-gray-700 border border-gray-200 shadow-lg'
                    : 'bg-gradient-to-r from-gray-700 via-gray-700 to-gray-500 text-white',
                isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
            style={{
                top: pos?.top ?? -9999,
                left: pos?.flip ? undefined : (pos?.left ?? -9999),
                right: pos?.flip ? (window.innerWidth - (pos?.left ?? 0)) : undefined,
                transform: 'translateY(-50%)',
                maxWidth: pos
                    ? Math.min(
                          width ? parseInt(width) : 99999,
                          pos.flip ? pos.left - 16 : window.innerWidth - pos.left - 16,
                      )
                    : undefined,
            }}
        >
            <div
                className={classNames(
                    'h-3 w-3 absolute',
                    variant === 'light' && 'bg-white border-gray-200',
                    variant === 'light' && (pos?.flip ? 'border-t border-r' : 'border-l border-b'),
                    variant === 'dark' && (pos?.flip ? 'bg-gray-500' : 'bg-gray-700'),
                )}
                style={{
                    [pos?.flip ? 'right' : 'left']: '-6px',
                    top: '50%',
                    transform: 'translateY(-50%) rotate(45deg)',
                }}
            />
            {tooltipText}
        </div>,
        document.body,
    );

    return (
        <div
            ref={triggerRef}
            className="relative flex items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            aria-describedby={tooltipText ? tooltipId.current : undefined}
        >
            {children}
            {tooltip}
        </div>
    );
};

export default Tooltip;
